/**
 * SAT HPA™ · Frontend API Client (Block 11.1)
 *
 * Kein lokales Scoring / Routing / Synthesis / Permission (§ 3).
 * Verwendet den zentralen `api`-Axios-Client (Auth-Bearer via Interceptor).
 * Nutzt ausschließlich Backend-Endpunkte aus:
 *   - hpa_api/access_router.py
 *   - hpa_api/client_router.py
 *   - hpa_api/mentor_router.py
 *   - hpa_api/report_router.py
 */
import { api } from "./api";

// ============================================================================
// ACCESS (Block 11.1)
// ============================================================================
export const hpaGetAccess = () =>
  api.get("/hpa/access").then((r) => r.data);

// ============================================================================
// ASSESSMENT LIFECYCLE (Block 11.2)
// ============================================================================
export const hpaCreateAssessment = (payload = {}) =>
  api.post("/hpa/assessments", payload).then((r) => r.data);

export const hpaStartAssessment = (id) =>
  api.post(`/hpa/assessments/${id}/start`).then((r) => r.data);

export const hpaNextQuestion = (id) =>
  api.get(`/hpa/assessments/${id}/next-question`).then((r) => r.data);

export const hpaSubmitAnswer = (id, payload) =>
  api.post(`/hpa/assessments/${id}/answers`, payload).then((r) => r.data);

export const hpaPauseAssessment = (id) =>
  api.post(`/hpa/assessments/${id}/pause`).then((r) => r.data);

export const hpaResumeAssessment = (id) =>
  api.post(`/hpa/assessments/${id}/resume`).then((r) => r.data);

export const hpaFinalizeAssessment = (id) =>
  api.post(`/hpa/assessments/${id}/finalize`).then((r) => r.data);

export const hpaAssessmentProgress = (id) =>
  api.get(`/hpa/assessments/${id}/progress`).then((r) => r.data);

// ============================================================================
// PROFILE (Block 11.3)
// ============================================================================
export const hpaGetProfile = (profileId) =>
  api.get(`/hpa/profiles/${profileId}`).then((r) => r.data);

// ============================================================================
// MENTOR (Block 11.4)
// ============================================================================
export const hpaMentorClients = () =>
  api.get("/hpa/mentor/clients").then((r) => r.data);

export const hpaMentorProfile = (profileId) =>
  api.get(`/hpa/mentor/profiles/${profileId}`).then((r) => r.data);

export const hpaMentorRelease = (profileId, reason = "") =>
  api
    .post(`/hpa/mentor/profiles/${profileId}/release`, { reason })
    .then((r) => r.data);

// ============================================================================
// SIGNED REPORT DOWNLOAD (Block 10a + Block 11.3)
// ============================================================================
export const hpaIssueReportToken = (profileId) =>
  api
    .post(`/hpa/profiles/${profileId}/report/download-token`, {})
    .then((r) => r.data);

/**
 * Signed Report Download UX (§ 29–30 · § 49):
 *   click → request signed access → short-lived download.
 *
 * Bei abgelaufenem Link automatisch neuen anfordern, wenn User berechtigt.
 * Kein technischer Token-Fehler dem Kunden zeigen.
 */
export const hpaDownloadReport = async (profileId) => {
  const doOnce = async () => {
    const { url } = await hpaIssueReportToken(profileId);
    // absolute URL bauen – wir dürfen keinen internen Pfad exponieren
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    return `${backend}${url}`;
  };

  const absoluteUrl = await doOnce();
  // Direkt-Download via axios (Auth-Header bleibt attached durch Interceptor)
  const resp = await api.get(absoluteUrl.replace(/^.*\/api/, ""), {
    responseType: "blob",
  });
  const blob = new Blob([resp.data], { type: "application/pdf" });
  const dispo = resp.headers?.["content-disposition"] || "";
  const match = /filename="([^"]+)"/.exec(dispo);
  const filename = match ? match[1] : `hpa-report-${profileId.slice(0, 8)}.pdf`;

  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
  return { filename };
};
